import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { Copy, ExternalLink, Network, Link as LinkIcon, Coins } from 'lucide-react';

const TestnetContainer = styled.div`
    width: 100%;
    color: white;
    font-family: Very Sans, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    position: relative;
`;

const TestnetWrapper = styled.div`
    width: 80%;
    margin: 0 auto;
    padding: 2rem 0;
    @media screen and (max-width: 768px) {
    width: 90%;
    padding: 1rem 0;
    }
`;

const SectionTitleRow = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-start;
    border-bottom: 1px solid rgba(255, 255, 255, 0.3);
    margin-bottom: 2rem;
    @media screen and (max-width: 768px) {
    border-bottom: none;
    margin-bottom: 1rem;
    }
`;

const SectionTitle = styled.h2`
    font-size: 42px;
    font-weight: 500;
    margin-bottom: 24px;
    color: white;
    font-family: Very Sans Headline, Sans Serif;

    .highlight {
        background: linear-gradient(135deg, #3B82F6 0%, #a78bfa 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
    }
`;

const GridContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const InfoCard = styled.div`
    background: rgba(255, 255, 255, 0.025);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    padding: 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.04);
        border-color: rgba(59, 130, 246, 0.3);
    }
`;

const IconWrapper = styled.div`
    width: 40px;
    height: 40px;
    border-radius: 8px;
    background: rgba(59, 130, 246, 0.1);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
`;

const InfoContent = styled.div`
    flex: 1;
    min-width: 0;
`;

const InfoLabel = styled.div`
    color: #94A3B8;
    font-size: 12px;
    font-weight: 400;
    margin-bottom: 0.25rem;
    font-family: Very Sans, Sans Serif;
`;

const InfoValue = styled.div`
    color: #ffffff;
    font-size: 14px;
    font-weight: 500;
    font-family: Very Sans, Sans Serif;
    word-break: break-all;
    @media screen and (max-width: 768px) {
    font-size: 13px;}
`;

const CopyButton = styled.button`
    background: transparent;
    border: none;
    color: #94A3B8;
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: color 0.3s ease;
    flex-shrink: 0;
    
    &:hover {
        color: #3B82F6;
    }
`;

const ActionCard = styled.div`
    background: rgba(255, 255, 255, 0.025);
    border: 1px solid rgba(255, 255, 255, 0.05);
    border-radius: 8px;
    padding: 1.5rem;
    transition: all 0.3s ease;
    
    &:hover {
        background: rgba(255, 255, 255, 0.04);
        border-color: rgba(59, 130, 246, 0.3);
    }
`;

const ActionTitle = styled.h3`
    font-family: Very Sans Bold, Sans Serif;
    font-size: 18px;
    color: #ffffff;
    margin: 0 0 0.5rem 0;
`;

const ActionDescription = styled.p`
    color: #94A3B8;
    font-size: 13px;
    font-family: Very Sans, Sans Serif;
    margin: 0 0 1rem 0;
    line-height: 1.5;
`;

const ActionButton = styled.button`
    background: #3B82F6;
    color: white;
    border: none;
    padding: 10px 20px;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: background 0.3s ease;
    font-family: Very Sans, Sans Serif;
    font-size: 14px;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    
    &:hover {
        background: #2563EB;
    }
`;

const SecondaryButton = styled(Link)`
    background: transparent;
    color: #94A3B8;
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 10px 20px;
    border-radius: 5px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: Very Sans, Sans Serif;
    font-size: 14px;
    width: 92.5%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    text-decoration: none;
    
    &:hover {
        background: rgba(59, 130, 246, 0.1);
        border-color: rgba(59, 130, 246, 0.3);
        color: #ffffff;
    }
    
    @media screen and (max-width: 768px) {
    width: 87.5%;
    }
`;

const GradientOrb1 = styled.div`
    position: absolute;
    top: -20%;
    left: -10%;
    width: 600px;
    height: 600px;
    background: radial-gradient(circle, rgba(59, 130, 246, 0.1) 0%, transparent 70%);
    pointer-events: none;
`;

const Testnet = () => {
    const networkInfo = {
        networkName: "Hydrix Testnet",
        rpcUrl: "https://chainfree.info:5010/validator/1",
        chainId: "1337",
        currencySymbol: "HYX",
        blockExplorer: "https://www.hydrix.online"
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        // You could add a toast notification here
    };

    const goToFaucet = () => {
        window.open(
            "https://www.hydrix.online",
            "_blank",
            "noopener, noreferrer"
        );
    };

    return (
        <TestnetContainer>
            <GradientOrb1 />
            <TestnetWrapper>
                <SectionTitleRow>
                    <SectionTitle>Testnet: <span className="highlight">Live</span> </SectionTitle>
                </SectionTitleRow>

                <GridContainer>
                    <InfoCard>
                        <IconWrapper>
                            <Network size={20} color="#3B82F6" />
                        </IconWrapper>
                        <InfoContent>
                            <InfoLabel>Network Name</InfoLabel>
                            <InfoValue>{networkInfo.networkName}</InfoValue>
                        </InfoContent>
                        <CopyButton onClick={() => copyToClipboard(networkInfo.networkName)}>
                            <Copy size={16} />
                        </CopyButton>
                    </InfoCard>

                    <InfoCard>
                        <IconWrapper>
                            <LinkIcon size={20} color="#3B82F6" />
                        </IconWrapper>
                        <InfoContent>
                            <InfoLabel>RPC URL</InfoLabel>
                            <InfoValue>{networkInfo.rpcUrl}</InfoValue>
                        </InfoContent>
                        <CopyButton onClick={() => copyToClipboard(networkInfo.rpcUrl)}>
                            <Copy size={16} />
                        </CopyButton>
                    </InfoCard>

                    <InfoCard>
                        <IconWrapper>
                            <Network size={20} color="#3B82F6" />
                        </IconWrapper>
                        <InfoContent>
                            <InfoLabel>Chain ID</InfoLabel>
                            <InfoValue>{networkInfo.chainId}</InfoValue>
                        </InfoContent>
                        <CopyButton onClick={() => copyToClipboard(networkInfo.chainId)}>
                            <Copy size={16} />
                        </CopyButton>
                    </InfoCard>

                    <InfoCard>
                        <IconWrapper>
                            <Coins size={20} color="#3B82F6" />
                        </IconWrapper>
                        <InfoContent>
                            <InfoLabel>Currency Symbol</InfoLabel>
                            <InfoValue>{networkInfo.currencySymbol}</InfoValue>
                        </InfoContent>
                        <CopyButton onClick={() => copyToClipboard(networkInfo.currencySymbol)}>
                            <Copy size={16} />
                        </CopyButton>
                    </InfoCard>
                </GridContainer>

                <GridContainer>
                    <ActionCard>
                        <ActionTitle>Block Explorer</ActionTitle>
                        <ActionDescription>
                            View transactions, blocks, and accounts on the Hydrix testnet.
                        </ActionDescription>
                        <SecondaryButton to={networkInfo.blockExplorer} target="_blank">
                            <ExternalLink size={14} />
                            Open Explorer
                        </SecondaryButton>
                    </ActionCard>

                    <ActionCard>
                        <ActionTitle>Testnet Faucet</ActionTitle>
                        <ActionDescription>
                            Get free testnet tokens to start building and testing.
                        </ActionDescription>
                        <ActionButton onClick={goToFaucet}>
                            Get Test Tokens
                        </ActionButton>
                    </ActionCard>
                </GridContainer>
            </TestnetWrapper>
        </TestnetContainer>
    );
};

export default Testnet;